import { useMemo } from "react";
import { Bill } from "@common/types/bill";
import { InteractiveScheduleCellState } from "@components/invoices/views/scheduleCellState";
import {
    BillScheduleCellState,
    isBillScheduleCellInteractive,
    resolveBillScheduleCellState,
    resolveBillScheduleReplaceScope,
} from "./billScheduleCellState";
import { BillScheduleCallbacks } from "./useBillScheduleCallbacks";
import { useBillSchedulePicker } from "./useBillSchedulePicker";

type PickerWiring = ReturnType<typeof useBillSchedulePicker>;

type BillRowSchedule = PickerWiring &
    (
        | { kind: "interactive"; state: InteractiveScheduleCellState }
        | {
              kind: "static";
              state: Exclude<
                  BillScheduleCellState,
                  InteractiveScheduleCellState
              >;
          }
    );

/** A bill's schedule state and picker wiring, shared by the Schedule cell and the details panel. */
export function useBillRowSchedule({
    bill,
    callbacks,
}: {
    bill: Bill;
    callbacks: Partial<BillScheduleCallbacks> | undefined;
}): BillRowSchedule {
    const state = useMemo(() => resolveBillScheduleCellState(bill), [bill]);
    const interactiveState = isBillScheduleCellInteractive(state)
        ? state
        : null;

    const scope = useMemo(
        () => ({
            ...resolveBillScheduleReplaceScope({
                bill,
                scheduleApplicationId:
                    state.kind === "applied"
                        ? state.scheduleApplicationId
                        : null,
            }),
            sourceLabel: bill.invoiceNumber ?? "Bill",
        }),
        [bill, state],
    );

    const picker = useBillSchedulePicker({
        bill,
        state: interactiveState,
        scope,
        callbacks,
    });

    if (isBillScheduleCellInteractive(state)) {
        return { ...picker, kind: "interactive", state };
    }
    return { ...picker, kind: "static", state };
}
